import React, { useState } from 'react';

const GROUPS_FAQ_URL = 'https://help.erg.zone/article/255-group-faq';
const BILLING_URL = 'https://admin.erg.zone/billing';

const groupTiers = [
  { maxMembers: 2, monthly: 5, yearly: 50 },
  { maxMembers: 5, monthly: 9, yearly: 90 },
  { maxMembers: 10, monthly: 15, yearly: 150 },
  { maxMembers: 25, monthly: 35, yearly: 350 },
  { maxMembers: 50, monthly: 55, yearly: 550 },
  { maxMembers: 100, monthly: 85, yearly: 850 },
  { maxMembers: 175, monthly: 115, yearly: 1150 },
  { maxMembers: 250, monthly: 145, yearly: 1450 },
];

const MAX_TIER = groupTiers[groupTiers.length - 1];

// Find the smallest tier that fits the team size. For teams over 250 members,
// extrapolate at +$30/mo per additional 100 members (matches the existing
// custom pricing model used elsewhere on the site).
const getTierForSize = (size) => {
  const safeSize = Math.max(1, Math.floor(Number(size) || 1));
  const tier = groupTiers.find((t) => safeSize <= t.maxMembers);
  if (tier) return tier;
  const extraBlocks = Math.ceil((safeSize - MAX_TIER.maxMembers) / 100);
  const extraMonthly = extraBlocks * 30;
  return {
    maxMembers: MAX_TIER.maxMembers + extraBlocks * 100,
    monthly: MAX_TIER.monthly + extraMonthly,
    yearly: (MAX_TIER.monthly + extraMonthly) * 10,
    custom: true,
  };
};

const freeFeatures = [
  'Automatic Session Management',
  'Unlimited Custom Workouts',
  'Set targets for pace & rate',
  'Real-Time Metrics',
  'Table, Grid, and Live Views',
  'Heart Rate Zones',
  'Integration with HR via the machine',
  'Bookmark Favorite Workouts',
  'Estimation for Workout Duration',
  'Benchmarks for 2k/4k and others',
  'Free ErgZone Challenges',
  'Weekly Samples from Coaches',
  'Concept2 Logbook Integration',
];

const proFeatures = [
  'Unlimited Historical Data',
  'Advanced Performance Metrics',
  'Desktop Web Admin Access',
  'HR & Power Graphs',
  'Integration with TrainingPeaks, Intervals.icu, Strava, and Rojabo',
  'MultiErg Training Circuits',
  'Cross-Training FIT Workouts',
  'AMRAP & Time Cap Engine',
  'Intensity Targets based on Power, FTP, or SPI',
  'Dynamic Interval Intensity',
  'Ghost Pacing (ReRow / ReSki / ReRide / ReRun)',
  'Preferred Layout Memory',
  'Full Apple Watch Integration',
  'Integration with HR via the app',
  'Premium Third-Party Syncing',
  'Download CSV and FIT File',
  'Create Private Training Plans',
];

const teamFeaturesHomepage = [
  'Real-Time Team Metrics',
  'Automated Workout Delivery',
  'Group Leaderboards & Challenges',
  'In-App Group Chat',
  'Includes Premium for the Owner',
  'Dynamic Team-Size Pricing',
];

const teamFeaturesGroups = [
  'Automated Workout Delivery',
  'Zero-Confusion Interval Programming',
  'Live Dashboard for Real-Time Team Metrics',
  'Group & Individual Total Volume Tracking',
  'Advanced Admin Analytics',
  'Detailed Performance Data Export (.CSV/Spreadsheet)',
  'In-App Group Chat & Direct Messages',
  'Flexible Member Management',
  'Interactive Group Leaderboards',
  'Custom Branding Options',
  'Dynamic Team-Size Pricing',
  'Dedicated Priority Support',
];

const PRO_MONTHLY = 4.99;
const PRO_YEARLY = 49.99;

const formatPrice = (value) =>
  Number.isInteger(value) ? `${value}` : value.toFixed(2);

const openGroupBeacon = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  const content = `I'd like to set up a Group on ErgZone.\n\n Group Name:\n Group Type (gym / studio / club / team / coaching / family):\n Approximate Member Count:\n What would you like to use Groups for?`;
  if (typeof window !== 'undefined' && window.Beacon) {
    window.Beacon('prefill', {
      subject: 'New Group',
      text: content,
    });
    window.Beacon('navigate', '/ask/message');
    window.Beacon('open');
  }
};

const parseTeamSize = (value) => Math.max(1, Math.floor(Number(value) || 1));

const TeamPricingCard = ({
  groupsOnly,
  groupPrice,
  teamMain,
  teamAlt,
  isAnnual,
}) => {
  const headerBlock = (
    <>
      <span className="card-tag">Group Admin Plan</span>
      <div className="card-title-row">
        <h3 className="card-title">Teams & Business</h3>
      </div>
      <div className="card-price">${formatPrice(teamMain)}</div>
      <p className="card-price-suffix">
        {isAnnual ? '/billed annually' : '/billed monthly'}
      </p>
      <p className="card-secondary-price">
        or ${formatPrice(teamAlt)} billed {isAnnual ? 'monthly' : 'annually'}
        {groupPrice.custom ? ' (custom)' : ''}
      </p>
      <p className="card-description">
        {groupsOnly
          ? 'Everything you need to program workouts, coach in real time, and grow your community—plus Premium for the admin.'
          : 'Team control made simple. Free access for athletes, full Premium & management features for admin.'}
      </p>
      {groupPrice.custom ? (
        <button type="button" className="card-cta" onClick={openGroupBeacon}>
          Contact us
        </button>
      ) : (
        <a
          href={BILLING_URL}
          className="card-cta"
          target="_blank"
          rel="noopener noreferrer"
        >
          {groupsOnly ? 'Start your group' : 'Get started'}
        </a>
      )}
    </>
  );

  const featuresBlock = (
    <>
      <p className="card-features-intro">Premium access for Owner, plus:</p>
      <ul className="card-features">
        {(groupsOnly ? teamFeaturesGroups : teamFeaturesHomepage).map(
          (feature) => (
            <li key={feature}>
              <span className="check">✓</span>
              {feature}
            </li>
          )
        )}
      </ul>
    </>
  );

  if (groupsOnly) {
    return (
      <div className="standalone-grid">
        <div className="standalone-left">{headerBlock}</div>
        <div className="standalone-right">{featuresBlock}</div>
      </div>
    );
  }

  return (
    <>
      {headerBlock}
      <hr className="card-divider" />
      {featuresBlock}
    </>
  );
};

const PricingPlans = ({ groupsOnly = false }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [teamSizeInput, setTeamSizeInput] = useState('50');

  const isAnnual = billingPeriod === 'annual';
  const teamSize = parseTeamSize(teamSizeInput);
  const groupPrice = getTierForSize(teamSize);

  const proMain = isAnnual ? PRO_YEARLY : PRO_MONTHLY;
  const proAlt = isAnnual ? PRO_MONTHLY : PRO_YEARLY;
  const teamMain = isAnnual ? groupPrice.yearly : groupPrice.monthly;
  const teamAlt = isAnnual ? groupPrice.monthly : groupPrice.yearly;

  const handleTeamSizeChange = (event) => {
    setTeamSizeInput(event.target.value);
  };

  const handleTeamSizeBlur = () => {
    setTeamSizeInput(String(parseTeamSize(teamSizeInput)));
  };

  return (
    <>
      <style>{`
        .pricing-section { background: #ffffff; }
        .pricing-heading h2 {
          font-family: 'Nunito', sans-serif;
          font-weight: 800;
        }
        .pricing-heading .accent { color: #00c9be; }

        .billing-toggle-wrap {
          display: flex;
          justify-content: center;
          margin: 24px 0 40px;
        }
        .billing-toggle {
          display: inline-flex;
          background: #f1f3f5;
          border-radius: 999px;
          padding: 4px;
        }
        .billing-toggle-btn {
          border: 0;
          background: transparent;
          color: #495057;
          font-family: 'Nunito', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          padding: 10px 22px;
          border-radius: 999px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .billing-toggle-btn:focus { outline: none; }
        .billing-toggle-btn.active {
          background: #00c9be;
          color: #ffffff;
          box-shadow: 0 2px 6px rgba(0, 158, 154, 0.25);
        }
        .billing-toggle-btn .discount-badge {
          background: #1f2937;
          color: #ffffff;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 999px;
          letter-spacing: 0.5px;
        }
        .billing-toggle-btn.active .discount-badge { background: #0f172a; }

        .pricing-card-wrap { position: relative; padding-top: 32px; }
        .pricing-card {
          position: relative;
          background: #ffffff;
          border: 2px solid #e9ecef;
          border-radius: 18px;
          padding: 32px 28px;
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .pricing-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }
        .pricing-card.popular {
          background: #00c9be;
          border-color: #00c9be;
          color: #ffffff;
        }
        .pricing-card.popular .card-tag { background: #1f2937; color: #ffffff; }
        .pricing-card.popular .card-description,
        .pricing-card.popular .card-secondary-price,
        .pricing-card.popular .card-features-intro,
        .pricing-card.popular .card-features li { color: rgba(255, 255, 255, 0.92); }
        .pricing-card.popular .card-price,
        .pricing-card.popular .card-title { color: #ffffff; }
        .pricing-card.popular .card-cta {
          background: #ffffff;
          color: #1f2937;
          border-color: #ffffff;
        }
        .pricing-card.popular .card-cta:hover {
          background: #f8f9fa;
          color: #009e9a;
        }
        .pricing-card.popular .card-features .check {
          background: rgba(255, 255, 255, 0.22);
          color: #ffffff;
        }
        .pricing-card.popular .card-divider {
          border-color: rgba(255, 255, 255, 0.3);
        }

        .card-tag {
          display: inline-block;
          background: #1f2937;
          color: #ffffff;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 999px;
          letter-spacing: 0.5px;
          margin-bottom: 14px;
          align-self: flex-start;
        }
        .card-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 4px;
        }
        .card-title {
          font-family: 'Nunito', sans-serif;
          font-weight: 800;
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0;
        }
        .popular-badge {
          background: #1f2937;
          color: #ffffff;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 999px;
          letter-spacing: 0.5px;
        }
        .card-price {
          font-family: 'Nunito', sans-serif;
          font-weight: 800;
          font-size: 2.75rem;
          color: #00c9be;
          line-height: 1.1;
          margin: 8px 0 4px;
        }
        .card-price-suffix {
          font-size: 0.9rem;
          color: #6c757d;
          margin-left: 4px;
        }
        .card-secondary-price {
          font-size: 0.85rem;
          color: #6c757d;
          margin-bottom: 14px;
        }
        .card-description {
          color: #495057;
          font-size: 0.95rem;
          margin-bottom: 18px;
          min-height: 72px;
        }
        .card-cta {
          display: block;
          text-align: center;
          background: #00c9be;
          color: #ffffff;
          border: 2px solid #00c9be;
          border-radius: 999px;
          padding: 12px 20px;
          font-family: 'Nunito', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          letter-spacing: 0.5px;
          text-decoration: none;
          cursor: pointer;
          width: 100%;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .card-cta:hover,
        .card-cta:focus {
          background: #009e9a;
          border-color: #009e9a;
          color: #ffffff;
          text-decoration: none;
        }
        .card-divider {
          border: 0;
          border-top: 1px solid #e9ecef;
          margin: 22px 0 16px;
        }
        .card-features-intro {
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 12px;
          font-size: 0.95rem;
        }
        .card-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .card-features li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 6px 0;
          color: #495057;
          font-size: 0.92rem;
        }
        .card-features .check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(0, 201, 190, 0.15);
          color: #00c9be;
          font-size: 0.7rem;
          font-weight: 700;
          margin-top: 2px;
        }
        .team-size-floating {
          position: absolute;
          top: 0;
          right: 12px;
          background: #f1f3f5;
          border-radius: 14px;
          padding: 10px 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.82rem;
          color: #495057;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
          z-index: 2;
        }
        .team-size-floating input {
          width: 60px;
          border: 1px solid #d0d5db;
          border-radius: 8px;
          padding: 4px 6px;
          font-family: 'Nunito', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          color: #1f2937;
          background: #ffffff;
        }
        .team-size-floating input:focus {
          outline: none;
          border-color: #00c9be;
          box-shadow: 0 0 0 3px rgba(0, 201, 190, 0.2);
        }
        .team-size-floating label {
          margin: 0;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.2;
        }

        .pricing-footnotes p {
          font-size: 0.85rem;
          color: #6c757d;
        }

        .pricing-card.standalone {
          padding: 44px 48px 40px;
        }
        .pricing-card.standalone .standalone-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
          gap: 12px 56px;
          align-items: start;
        }
        .pricing-card.standalone .standalone-left {
          display: flex;
          flex-direction: column;
        }
        .pricing-card.standalone .standalone-right {
          padding-left: 32px;
          border-left: 1px solid #e9ecef;
        }
        .pricing-card.standalone .card-description {
          min-height: 0;
          font-size: 1rem;
        }
        .pricing-card.standalone .card-price {
          font-size: 3.25rem;
        }
        .pricing-card.standalone .card-cta {
          align-self: flex-start;
          width: auto;
          padding: 12px 32px;
        }
        .pricing-card.standalone .card-features-intro {
          margin-top: 0;
        }
        .pricing-card.standalone .card-features {
          column-count: 2;
          column-gap: 28px;
        }
        .pricing-card.standalone .card-features li {
          break-inside: avoid;
        }

        @media (max-width: 991px) {
          .team-size-floating {
            position: static;
            margin: 0 0 16px;
            align-self: flex-start;
          }
          .pricing-card-wrap { padding-top: 0; }
          .card-description { min-height: 0; }
          .pricing-card.standalone {
            padding: 32px 24px 28px;
          }
          .pricing-card.standalone .standalone-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .pricing-card.standalone .standalone-right {
            padding-left: 0;
            padding-top: 24px;
            border-left: 0;
            border-top: 1px solid #e9ecef;
          }
          .pricing-card.standalone .card-features {
            column-count: 1;
          }
          .pricing-card.standalone .card-cta {
            align-self: stretch;
            width: 100%;
          }
        }
      `}</style>

      <section className="features pricing-section" id="pricing">
        <div className="container">
          <div className="section-heading pricing-heading text-center">
            {groupsOnly ? (
              <>
                <h2>
                  <span className="accent">A Plan</span> for Every Group
                </h2>
                <p className="text-muted">
                  Pricing scales with your roster—from small private circles to
                  commercial gyms and studios.
                </p>
              </>
            ) : (
              <>
                <h2>
                  <span className="accent">Plans for Every</span> Athlete and
                  Every Team
                </h2>
                <p className="text-muted">
                  Tailored plans for solo athletes, fitness enthusiasts, and
                  entire businesses.
                </p>
              </>
            )}
          </div>

          <div
            className="billing-toggle-wrap"
            role="tablist"
            aria-label="Billing period"
          >
            <div className="billing-toggle">
              <button
                type="button"
                role="tab"
                aria-selected={!isAnnual}
                className={`billing-toggle-btn ${!isAnnual ? 'active' : ''}`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly plan
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isAnnual}
                className={`billing-toggle-btn ${isAnnual ? 'active' : ''}`}
                onClick={() => setBillingPeriod('annual')}
              >
                Annual plan{' '}
                <span className="discount-badge">2 MONTHS FREE</span>
              </button>
            </div>
          </div>

          <div className="row justify-content-center">
            {!groupsOnly && (
              <>
                {/* Free plan */}
                <div className="col-lg-4 col-md-6 mb-4">
                  <div className="pricing-card-wrap h-100">
                    <div className="pricing-card">
                      <span className="card-tag">App for athletes</span>
                      <div className="card-title-row">
                        <h3 className="card-title">Free</h3>
                      </div>
                      <div className="card-price">$0</div>
                      <p className="card-price-suffix">that means Free!</p>
                      <p
                        className="card-secondary-price"
                        aria-hidden="true"
                        style={{ visibility: 'hidden' }}
                      >
                        &nbsp;
                      </p>
                      <p className="card-description">
                        All the essential tools you need to kick off your
                        training, completely free.
                      </p>
                      <a
                        href="https://erg.zone/#download"
                        className="card-cta"
                        style={{ background: 'transparent', color: '#00c9be' }}
                      >
                        Get started
                      </a>
                      <hr className="card-divider" />
                      <ul className="card-features">
                        {freeFeatures.map((feature) => (
                          <li key={feature}>
                            <span className="check">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Premium plan (highlighted) */}
                <div className="col-lg-4 col-md-6 mb-4">
                  <div className="pricing-card-wrap h-100">
                    <div className="pricing-card popular">
                      <span className="card-tag">
                        Advanced Athlete Platform
                      </span>
                      <div className="card-title-row">
                        <h3 className="card-title">Premium</h3>
                        <span className="popular-badge">Popular</span>
                      </div>
                      <div className="card-price">${formatPrice(proMain)}</div>
                      <p className="card-price-suffix">
                        {isAnnual ? '/billed annually' : '/billed monthly'}
                      </p>
                      <p className="card-secondary-price">
                        or ${formatPrice(proAlt)} billed{' '}
                        {isAnnual ? 'monthly' : 'annually'}
                      </p>
                      <p className="card-description">
                        Optimize your performance with the data you need to get
                        faster, stronger, and more efficient.
                      </p>
                      <a
                        href={BILLING_URL}
                        className="card-cta"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get started
                      </a>
                      <hr className="card-divider" />
                      <p className="card-features-intro">
                        Everything in Free Plan, plus:
                      </p>
                      <ul className="card-features">
                        {proFeatures.map((feature) => (
                          <li key={feature}>
                            <span className="check">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Teams and Business plan */}
            <div
              className={groupsOnly ? 'col-12 mb-4' : 'col-lg-4 col-md-12 mb-4'}
            >
              <div className="pricing-card-wrap h-100">
                <div className="team-size-floating">
                  <label htmlFor="team-size-input">
                    How big is your{groupsOnly ? ' ' : <br />}team?
                  </label>
                  <input
                    id="team-size-input"
                    type="number"
                    min="1"
                    value={teamSizeInput}
                    onChange={handleTeamSizeChange}
                    onBlur={handleTeamSizeBlur}
                    aria-label="Team size in members"
                  />
                  <span>members</span>
                </div>
                <div
                  className={`pricing-card ${groupsOnly ? 'standalone' : ''}`}
                >
                  <TeamPricingCard
                    groupsOnly={groupsOnly}
                    groupPrice={groupPrice}
                    teamMain={teamMain}
                    teamAlt={teamAlt}
                    isAnnual={isAnnual}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row pricing-footnotes">
            <div className="col-lg-10 mx-auto text-center mt-3">
              <p style={{ marginBottom: groupsOnly ? '6px' : 0 }}>
                Additional taxes may apply based on your location. No contracts,
                cancel anytime.
              </p>
              {groupsOnly && (
                <p>
                  Questions about pricing? See our{' '}
                  <a
                    href={GROUPS_FAQ_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#00c9be', textDecoration: 'underline' }}
                  >
                    Group Pricing FAQ
                  </a>
                  .
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingPlans;
